import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  Calendar,
  Car,
  Edit,
  Plus,
  Users,
} from 'lucide-react';
import {
  Avatar,
  Button,
  Card,
  Description,
  Heading,
  SearchField,
  Spinner,
  useOverlayState,
} from '@heroui/react';
import {
  createCondominium,
  listCondominiumsWithMemberCount,
  updateCondominium,
  type CondominiumFormPayload,
  type CondominiumWithMemberCount,
} from '../../../api/condominiums';
import { PageHeader } from '../../../components/page/page-header';
import { useAuth } from '../../../hooks/useAuth';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { CondominiumFormDrawer } from '../components/condominium-form-drawer';

const QUERY_KEY = ['admin', 'condominiums', 'with-member-count'] as const;

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Building2;
  label: string;
  value: number;
}) {
  return (
    <Card>
      <Card.Content className="flex items-center gap-3 p-4">
        <div className="flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Icon size={20} />
        </div>
        <div>
          <Description>{label}</Description>
          <Heading level={3} className="text-2xl font-semibold">
            {value}
          </Heading>
        </div>
      </Card.Content>
    </Card>
  );
}

function CondominiumCard({
  condominium,
  onEdit,
  onOpen,
}: {
  condominium: CondominiumWithMemberCount;
  onEdit?: () => void;
  onOpen: () => void;
}) {
  return (
    <Card className="transition-shadow hover:shadow-md">
      <Card.Header className="pb-2">
        <div className="flex items-start gap-3">
          <Avatar size="md" color="accent">
            <Avatar.Fallback>{getInitials(condominium.name)}</Avatar.Fallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <Card.Title className="truncate">{condominium.name}</Card.Title>
            <Card.Description>@{condominium.slug}</Card.Description>
          </div>
        </div>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 text-muted">
            <Users size={16} />
            <span>Membros:</span>
            <span className="font-medium text-foreground">{condominium.memberCount}</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <Calendar size={16} />
            <span>Criado em:</span>
            <span className="font-medium text-foreground">
              {new Date(condominium.createdAt).toLocaleDateString('pt-BR')}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="primary" className="flex-1" onPress={onOpen}>
            Ver detalhes
          </Button>
          {onEdit ? (
            <Button isIconOnly variant="secondary" aria-label="Editar" onPress={onEdit}>
              <Edit size={16} />
            </Button>
          ) : null}
        </div>
      </Card.Content>
    </Card>
  );
}

export default function AdminCondominiumsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userDoc } = useAuth();
  const isGlobalAdmin = userDoc?.isGlobalAdmin ?? false;
  const pageDescription = isGlobalAdmin
    ? 'Gerencie todos os condomínios cadastrados no sistema.'
    : 'Gerencie os condomínios que você administra.';
  const createDrawer = useOverlayState();
  const editDrawer = useOverlayState();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CondominiumWithMemberCount | null>(null);

  const { data: condominiums = [], isLoading, isError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listCondominiumsWithMemberCount,
    enabled: !!userDoc,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: createCondominium,
    onSuccess: async () => {
      await invalidate();
      showSuccess('Condomínio criado com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CondominiumFormPayload }) =>
      updateCondominium(id, payload),
    onSuccess: async () => {
      await invalidate();
      showSuccess('Condomínio atualizado com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const existingSlugs = useMemo(() => condominiums.map((c) => c.slug), [condominiums]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return condominiums;
    return condominiums.filter(
      (c) => c.name.toLowerCase().includes(term) || c.slug.toLowerCase().includes(term),
    );
  }, [condominiums, search]);

  const totalMembers = useMemo(
    () => condominiums.reduce((acc, c) => acc + c.memberCount, 0),
    [condominiums],
  );

  const avgMembers =
    condominiums.length > 0 ? Math.round(totalMembers / condominiums.length) : 0;

  const openCreate = () => {
    createDrawer.open();
  };

  const openEdit = (condominium: CondominiumWithMemberCount) => {
    setEditing(condominium);
    editDrawer.open();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Condomínios"
          description={pageDescription}
        />
        <div className="flex h-64 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Condomínios"
          description={pageDescription}
        />
        <Card className="max-w-md">
          <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
            <AlertCircle className="text-danger" size={40} />
            <Description>Não foi possível carregar os condomínios.</Description>
            <Button variant="primary" onPress={() => void refetch()}>
              Tentar novamente
            </Button>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Condomínios"
        description={pageDescription}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchField
          aria-label="Pesquisar condomínios"
          value={search}
          onChange={setSearch}
          className="w-full sm:max-w-md"
        >
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="Pesquisar por nome ou slug…" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>
        {isGlobalAdmin ? (
          <Button variant="primary" onPress={openCreate}>
            <Plus size={18} />
            Novo condomínio
          </Button>
        ) : null}
      </div>

      {isGlobalAdmin ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard icon={Building2} label="Total de condomínios" value={condominiums.length} />
          <StatCard icon={Users} label="Total de membros" value={totalMembers} />
          <StatCard icon={Car} label="Média de membros" value={avgMembers} />
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <Card>
          <Card.Content className="py-12 text-center">
            <Description>
              {search.trim()
                ? 'Nenhum condomínio encontrado para sua busca.'
                : isGlobalAdmin
                  ? 'Nenhum condomínio cadastrado. Crie o primeiro.'
                  : 'Você ainda não administra nenhum condomínio.'}
            </Description>
          </Card.Content>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {filtered.map((condominium) => (
            <CondominiumCard
              key={condominium.id}
              condominium={condominium}
              onEdit={isGlobalAdmin ? () => openEdit(condominium) : undefined}
              onOpen={() => navigate(`/admin/condominiums/${condominium.id}`)}
            />
          ))}
        </div>
      )}

      {isGlobalAdmin ? (
        <>
          <CondominiumFormDrawer
            state={createDrawer}
            mode="create"
            existingSlugs={existingSlugs}
            onSubmit={(payload) => createMutation.mutateAsync(payload)}
          />

          <CondominiumFormDrawer
            state={editDrawer}
            mode="edit"
            initialValues={
              editing ? { name: editing.name, slug: editing.slug } : undefined
            }
            onSubmit={(payload) => {
              if (!editing) return Promise.reject(new Error('Condomínio não selecionado.'));
              return updateMutation.mutateAsync({ id: editing.id, payload });
            }}
          />
        </>
      ) : null}
    </div>
  );
}
